import { useState, useEffect, useCallback } from 'react';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import StatusBadge from '../../components/common/StatusBadge';
import { usePaginacao } from '../../hooks/usePaginacao';
import { useSortable } from '../../hooks/useSortable';
import { useDebounce } from '../../hooks/useDebounce';
import { formatDateTime } from '../../utils/date';
import * as api from '../../api/auditLogs';

const ACAO_COR = {
  create:       'bg-success',
  update:       'bg-primary',
  vincular:     'bg-info text-dark',
  desvincular:  'bg-warning text-dark',
  cancelar:     'bg-danger',
  finalizar:    'bg-success',
};

function AcaoBadge({ acao }) {
  return <span className={`badge ${ACAO_COR[acao] || 'bg-secondary'}`}>{acao}</span>;
}

export default function Logs() {
  const [items, setItems]     = useState([]);
  const [entidades, setEntidades] = useState([]);
  const [acoes, setAcoes]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro]       = useState('');

  const [busca, setBusca]         = useState('');
  const [entidade, setEntidade]   = useState('');
  const [acao, setAcao]           = useState('');
  const [perfil, setPerfil]       = useState('');
  const buscaDebounced = useDebounce(busca, 400);

  useEffect(() => {
    api.listarEntidades().then(r => setEntidades(r.data)).catch(() => {});
    api.listarAcoes().then(r => setAcoes(r.data)).catch(() => {});
  }, []);

  const carregar = useCallback(() => {
    setLoading(true);
    setErro('');
    const params = {};
    if (buscaDebounced) params.busca = buscaDebounced;
    if (entidade) params.entidade = entidade;
    if (acao) params.acao = acao;
    if (perfil) params.perfil = perfil;
    api.listar(params)
      .then(r => setItems(r.data))
      .catch(() => setErro('Erro ao carregar logs. Tente novamente.'))
      .finally(() => setLoading(false));
  }, [buscaDebounced, entidade, acao, perfil]);

  useEffect(() => { carregar(); }, [carregar]);

  const { sorted, Th } = useSortable(items, 'criado_em');
  const { paginados, Paginacao } = usePaginacao(sorted);

  return (
    <div>
      <PageHeader titulo="Logs de Auditoria" subtitulo={`${items.length} registros (mais recentes primeiro, limite de 500)`} />

      <div className="d-flex gap-2 mb-3 flex-wrap">
        <input className="form-control" style={{ maxWidth: 280 }}
          placeholder="Buscar por e-mail ou descrição..."
          value={busca} onChange={e => setBusca(e.target.value)} />
        <select className="form-select" style={{ maxWidth: 200 }} value={entidade} onChange={e => setEntidade(e.target.value)}>
          <option value="">Todas as entidades</option>
          {entidades.map(e => <option key={e} value={e}>{e}</option>)}
        </select>
        <select className="form-select" style={{ maxWidth: 180 }} value={acao} onChange={e => setAcao(e.target.value)}>
          <option value="">Todas as ações</option>
          {acoes.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <select className="form-select" style={{ maxWidth: 160 }} value={perfil} onChange={e => setPerfil(e.target.value)}>
          <option value="">Todos os perfis</option>
          <option value="admin">Admin</option>
          <option value="gestor">Gestor</option>
          <option value="operador">Operador</option>
          <option value="cliente">Cliente</option>
        </select>
      </div>

      {erro && <div className="alert alert-danger">{erro}</div>}

      <div className="card">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead>
                <tr>
                  <Th campo="criado_em">Data/Hora</Th>
                  <Th campo="email">Usuário</Th>
                  <th>Perfil</th>
                  <th>Ação</th>
                  <Th campo="entidade">Entidade</Th>
                  <th>Descrição</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={6} className="text-center py-4"><LoadingSpinner /></td></tr>
                )}
                {!loading && paginados.length === 0 && (
                  <tr><td colSpan={6} className="text-center text-muted py-4">Nenhum log encontrado.</td></tr>
                )}
                {!loading && paginados.map(l => (
                  <tr key={l.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(l.criado_em)}</td>
                    <td>{l.email || '—'}</td>
                    <td>{l.perfil ? <StatusBadge status={l.perfil} /> : '—'}</td>
                    <td><AcaoBadge acao={l.acao} /></td>
                    <td>{l.entidade}</td>
                    <td style={{ maxWidth: 420 }}>{l.descricao || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <Paginacao />
    </div>
  );
}
